/**
 * AzureStorageTreeProvider
 *
 * Azure Blob Storage implementation of ITreeDataProvider
 *
 * This provider:
 * - Lists blobs from Azure Storage container
 * - Creates virtual folder structure from blob paths (e.g., "folder/file.txt")
 * - Supports creating/deleting/renaming blobs
 * - Uses quantum-auth for authentication
 * - Caches tree structure for performance
 */

import type { ITreeDataProvider } from './ITreeDataProvider.js';
import { TreeDataProviderError, TreeDataProviderErrorCode } from './TreeDataProviderError.js';
import {
  TreeNode,
  CreateNodeOptions,
  TreeDataProviderConfig,
  TreeProviderEventType,
  TreeProviderEventDetail,
  FileNameValidationResult
} from '../../types/index.js';

/**
 * Blob information from Azure Storage API
 */
interface BlobInfo {
  name: string;
  size: number;
  modified: Date;
  contentType: string;
}

/**
 * Configuration specific to Azure Storage provider
 */
export interface AzureStorageTreeProviderConfig extends TreeDataProviderConfig {
  /** Azure storage account name */
  storageAccount: string;

  /** Container name */
  container: string;

  /** Authentication method */
  authMethod: 'quantum-auth' | 'sas-token';

  /** SAS token (if using sas-token auth) */
  sasToken?: string;

  /** Whether to create virtual folders based on path separators */
  virtualFolders?: boolean;

  /** Cache TTL in milliseconds (0 = cache until refresh()) */
  cacheTTL?: number;
}

/**
 * Azure Blob Storage implementation of ITreeDataProvider
 */
export class AzureStorageTreeProvider implements ITreeDataProvider {
  private config: AzureStorageTreeProviderConfig;
  private authService: any;
  private cache: Map<string, TreeNode>;
  private eventHandlers: Map<TreeProviderEventType, Function[]>;
  private rootNodes: TreeNode[] | null;
  private lastRefresh: Date | null;

  constructor(config: AzureStorageTreeProviderConfig) {
    this.config = {
      virtualFolders: true,
      cacheTTL: 0,
      lazyLoad: false,
      ...config
    };

    this.cache = new Map();
    this.eventHandlers = new Map();
    this.rootNodes = null;
    this.lastRefresh = null;

    // Initialize authentication
    if (this.config.authMethod === 'quantum-auth') {
      this._initQuantumAuth();
    }
  }

  // ==================== PUBLIC: CONFIGURATION GETTERS ====================

  /**
   * Get the storage account name
   */
  get storageAccount(): string {
    return this.config.storageAccount;
  }

  /**
   * Get the container name
   */
  get container(): string {
    return this.config.container;
  }

  // ==================== PRIVATE: AUTHENTICATION ====================

  /**
   * Initialize quantum authentication
   */
  private async _initQuantumAuth(): Promise<void> {
    let getQuantumAuth = (window as any).getQuantumAuth;

    if (!getQuantumAuth) {
      // Wait for auth to initialize
      await new Promise((resolve) => {
        document.addEventListener('quantum-auth-initialized', resolve, { once: true });
      });
      // Re-fetch getQuantumAuth after event fires
      getQuantumAuth = (window as any).getQuantumAuth;
    }

    if (!getQuantumAuth) {
      throw new TreeDataProviderError(
        'quantum-auth not available after initialization',
        TreeDataProviderErrorCode.AUTH_FAILED
      );
    }

    this.authService = getQuantumAuth();
  }

  /**
   * Get authentication token
   */
  private async _getToken(): Promise<string> {
    if (this.config.authMethod === 'sas-token') {
      if (!this.config.sasToken) {
        throw new TreeDataProviderError(
          'SAS token not provided',
          TreeDataProviderErrorCode.AUTH_FAILED
        );
      }
      return this.config.sasToken;
    }

    if (this.config.authMethod === 'quantum-auth') {
      if (!this.authService) {
        // Try to initialize if not already done
        await this._initQuantumAuth();
      }

      if (!this.authService) {
        throw new TreeDataProviderError(
          'Authentication not initialized',
          TreeDataProviderErrorCode.AUTH_FAILED
        );
      }

      try {
        return await this.authService.getToken([
          'https://storage.azure.com/user_impersonation'
        ]);
      } catch (error) {
        throw new TreeDataProviderError(
          'Failed to get authentication token',
          TreeDataProviderErrorCode.AUTH_FAILED,
          undefined,
          error as Error
        );
      }
    }

    throw new TreeDataProviderError(
      'Invalid authentication method',
      TreeDataProviderErrorCode.AUTH_FAILED
    );
  }

  // ==================== PRIVATE: AZURE STORAGE API ====================

  /**
   * List all blobs from Azure Storage
   */
  private async _listBlobs(): Promise<BlobInfo[]> {
    console.log('[AzureStorageTreeProvider] Getting auth token...');
    const token = await this._getToken();
    console.log('[AzureStorageTreeProvider] Token obtained');

    const baseUrl = `https://${this.config.storageAccount}.blob.core.windows.net/${this.config.container}`;
    const url = `${baseUrl}?restype=container&comp=list`;
    console.log('[AzureStorageTreeProvider] Fetching from:', url);

    const headers: Record<string, string> = {
      'x-ms-version': '2020-04-08'
    };

    if (this.config.authMethod === 'quantum-auth') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, { headers });
      console.log('[AzureStorageTreeProvider] Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new TreeDataProviderError(
          `Failed to list blobs: ${response.statusText}`,
          TreeDataProviderErrorCode.NETWORK_ERROR,
          response.status
        );
      }

      const xmlText = await response.text();
      console.log('[AzureStorageTreeProvider] Response XML length:', xmlText.length);
      return this._parseListBlobsResponse(xmlText);
    } catch (error) {
      console.error('[AzureStorageTreeProvider] Error listing blobs:', error);
      if (error instanceof TreeDataProviderError) {
        throw error;
      }
      throw new TreeDataProviderError(
        'Network error while listing blobs',
        TreeDataProviderErrorCode.NETWORK_ERROR,
        undefined,
        error as Error
      );
    }
  }

  /**
   * Parse Azure Blob Storage XML response
   */
  private _parseListBlobsResponse(xmlText: string): BlobInfo[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'text/xml');
    const blobElements = doc.querySelectorAll('Blob');

    const blobs: BlobInfo[] = [];

    blobElements.forEach((blobEl) => {
      const name = blobEl.querySelector('Name')?.textContent || '';
      const properties = blobEl.querySelector('Properties');

      // Skip folder marker files
      if (name.endsWith('/.folder')) {
        return;
      }

      // Filter by base path if configured
      if (this.config.basePath && !name.startsWith(this.config.basePath)) {
        return;
      }

      // Filter by allowed extensions if configured
      if (this.config.allowedExtensions?.length) {
        const ext = '.' + name.split('.').pop()?.toLowerCase();
        if (!this.config.allowedExtensions.includes(ext)) {
          return;
        }
      }

      blobs.push({
        name,
        size: parseInt(properties?.querySelector('Content-Length')?.textContent || '0'),
        modified: new Date(properties?.querySelector('Last-Modified')?.textContent || ''),
        contentType: properties?.querySelector('Content-Type')?.textContent || ''
      });
    });

    return blobs;
  }

  /**
   * Put a blob to Azure Storage
   */
  private async _putBlob(
    path: string,
    content: string | Blob,
    contentType: string
  ): Promise<void> {
    const token = await this._getToken();
    const url = `https://${this.config.storageAccount}.blob.core.windows.net/${this.config.container}/${path}`;

    const body = typeof content === 'string'
      ? new Blob([content], { type: contentType })
      : content;

    const headers: Record<string, string> = {
      'x-ms-version': '2020-04-08',
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
      'Content-Length': body.size.toString()
    };

    if (this.config.authMethod === 'quantum-auth') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body
      });

      if (!response.ok) {
        throw new TreeDataProviderError(
          `Failed to upload blob: ${response.statusText}`,
          TreeDataProviderErrorCode.NETWORK_ERROR,
          response.status
        );
      }
    } catch (error) {
      if (error instanceof TreeDataProviderError) {
        throw error;
      }
      throw new TreeDataProviderError(
        'Network error while uploading blob',
        TreeDataProviderErrorCode.NETWORK_ERROR,
        undefined,
        error as Error
      );
    }
  }

  /**
   * Delete a blob from Azure Storage
   */
  private async _deleteBlob(path: string): Promise<void> {
    const token = await this._getToken();
    const url = `https://${this.config.storageAccount}.blob.core.windows.net/${this.config.container}/${path}`;

    const headers: Record<string, string> = {
      'x-ms-version': '2020-04-08'
    };

    if (this.config.authMethod === 'quantum-auth') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers
      });

      if (!response.ok && response.status !== 404) {
        throw new TreeDataProviderError(
          `Failed to delete blob: ${response.statusText}`,
          TreeDataProviderErrorCode.NETWORK_ERROR,
          response.status
        );
      }
    } catch (error) {
      if (error instanceof TreeDataProviderError) {
        throw error;
      }
      throw new TreeDataProviderError(
        'Network error while deleting blob',
        TreeDataProviderErrorCode.NETWORK_ERROR,
        undefined,
        error as Error
      );
    }
  }

  // ==================== PRIVATE: TREE BUILDING ====================

  /**
   * Build hierarchical tree structure from flat blob list
   */
  private _buildTreeFromBlobs(blobs: BlobInfo[]): TreeNode[] {
    const nodeMap = new Map<string, TreeNode>();
    const rootNodes: TreeNode[] = [];

    // Sort blobs by path for consistent ordering
    const sortedBlobs = [...blobs].sort((a, b) => a.name.localeCompare(b.name));

    sortedBlobs.forEach((blob) => {
      const pathParts = blob.name.split('/');
      let currentPath = '';
      let parentId: string | null = null;

      // Create folder nodes for each path segment
      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        const isFile = i === pathParts.length - 1;

        currentPath = currentPath ? `${currentPath}/${part}` : part;
        const nodeId = currentPath;

        // Skip if already created
        if (nodeMap.has(nodeId)) {
          parentId = nodeId;
          continue;
        }

        // Determine icon based on type and extension
        const icon = isFile
          ? this._getFileIcon(part)
          : this._getFolderIcon();

        const node: TreeNode = {
          id: nodeId,
          name: part,
          type: isFile ? 'file' : 'folder',
          path: currentPath,
          icon,
          parentId,
          items: isFile ? undefined : [],
          childrenLoaded: true,
          metadata: isFile ? {
            extension: part.split('.').pop(),
            size: blob.size,
            modified: blob.modified,
            contentType: blob.contentType
          } : undefined
        };

        // Add to parent's items or root
        if (parentId === null) {
          rootNodes.push(node);
        } else {
          const parent = nodeMap.get(parentId);
          if (parent && parent.items) {
            parent.items.push(node);
          }
        }

        nodeMap.set(nodeId, node);
        this.cache.set(nodeId, node);
        parentId = nodeId;
      }
    });

    return rootNodes;
  }

  /**
   * Get icon for file based on extension
   */
  private _getFileIcon(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'drawio':
        return 'assets/icons/diagram.svg';
      case 'svg':
        return 'assets/icons/image.svg';
      case 'png':
      case 'jpg':
      case 'jpeg':
        return 'assets/icons/image.svg';
      default:
        return 'assets/icons/file.svg';
    }
  }

  /**
   * Get folder icon
   */
  private _getFolderIcon(): string {
    return 'assets/icons/folder.svg';
  }

  // ==================== PRIVATE: PATH UTILITIES ====================

  /**
   * Normalize a path (remove leading/trailing slashes, collapse multiple slashes)
   */
  private _normalizePath(path: string): string {
    return path
      .replace(/^\/+/, '')   // Remove leading slashes
      .replace(/\/+$/, '')   // Remove trailing slashes
      .replace(/\/+/g, '/'); // Collapse multiple slashes
  }

  /**
   * Get parent path from a path
   */
  private _getParentPath(path: string): string | null {
    const normalized = this._normalizePath(path);
    const lastSlash = normalized.lastIndexOf('/');

    if (lastSlash === -1) {
      return null; // This is a root-level item
    }

    return normalized.substring(0, lastSlash);
  }

  /**
   * Join path parts
   */
  private _joinPath(...parts: string[]): string {
    return parts
      .filter(p => p)
      .map(p => this._normalizePath(p))
      .join('/');
  }

  // ==================== PRIVATE: CACHE MANAGEMENT ====================

  /**
   * Check if cache is valid
   */
  private _isCacheValid(): boolean {
    if (!this.lastRefresh || !this.rootNodes) {
      return false;
    }

    const cacheTtl = this.config.cacheTTL ?? 0;
    if (cacheTtl === 0) {
      return true; // Cache indefinitely until refresh() called
    }

    const now = new Date();
    const elapsed = now.getTime() - this.lastRefresh.getTime();
    return elapsed < cacheTtl;
  }

  /**
   * Invalidate cache
   */
  private _invalidateCache(): void {
    this.cache.clear();
    this.rootNodes = null;
    this.lastRefresh = null;
  }

  // ==================== PRIVATE: EVENT MANAGEMENT ====================

  /**
   * Emit an event
   */
  private _emit(event: TreeProviderEventType, detail: TreeProviderEventDetail): void {
    const handlers = this.eventHandlers.get(event) || [];
    handlers.forEach(handler => {
      try {
        handler(detail);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    });
  }

  // ==================== PRIVATE: FILE CONTENT ====================

  /**
   * Get default content for new diagram files
   */
  private _getDefaultFileContent(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();

    // Default empty diagram XML for .drawio files
    if (ext === 'drawio' || ext === 'xml') {
      return '<mxGraphModel dx="879" dy="557" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="850" pageHeight="1100" math="0" shadow="0"><root><mxCell id="0"/><mxCell id="1" parent="0"/></root></mxGraphModel>';
    }

    return '';
  }

  /**
   * Get content type for a file
   */
  private _getContentType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();

    const mimeTypes: Record<string, string> = {
      'drawio': 'application/xml',
      'xml': 'application/xml',
      'svg': 'image/svg+xml',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg'
    };

    return mimeTypes[ext || ''] || 'application/octet-stream';
  }

  // ==================== PUBLIC: READ OPERATIONS ====================

  /**
   * Get root-level nodes
   */
  async getRootNodes(): Promise<TreeNode[]> {
    console.log('[AzureStorageTreeProvider] getRootNodes called');

    // Check cache
    if (this._isCacheValid() && this.rootNodes) {
      console.log('[AzureStorageTreeProvider] Returning cached nodes:', this.rootNodes.length);
      return this.rootNodes;
    }

    // Fetch blobs and build tree
    console.log('[AzureStorageTreeProvider] Fetching blobs...');
    const blobs = await this._listBlobs();
    console.log('[AzureStorageTreeProvider] Fetched blobs:', blobs.length);

    this.rootNodes = this._buildTreeFromBlobs(blobs);
    console.log('[AzureStorageTreeProvider] Built tree with root nodes:', this.rootNodes.length);

    this.lastRefresh = new Date();

    return this.rootNodes;
  }

  /**
   * Get children of a folder node
   */
  async getChildren(nodeId: string): Promise<TreeNode[]> {
    const node = this.cache.get(nodeId);

    if (!node) {
      throw new TreeDataProviderError(
        'Node not found',
        TreeDataProviderErrorCode.NOT_FOUND
      );
    }

    if (node.type !== 'folder') {
      throw new TreeDataProviderError(
        'Node is not a folder',
        TreeDataProviderErrorCode.INVALID_OPERATION
      );
    }

    return node.items || [];
  }

  /**
   * Get a specific node by ID
   */
  async getNode(nodeId: string): Promise<TreeNode | null> {
    // Ensure tree is loaded
    if (!this.rootNodes) {
      await this.getRootNodes();
    }

    return this.cache.get(nodeId) || null;
  }

  /**
   * Get file content as blob
   */
  async getFileContent(nodeId: string): Promise<Blob> {
    const node = await this.getNode(nodeId);

    if (!node) {
      throw new TreeDataProviderError(
        'File not found',
        TreeDataProviderErrorCode.NOT_FOUND
      );
    }

    if (node.type !== 'file') {
      throw new TreeDataProviderError(
        'Node is not a file',
        TreeDataProviderErrorCode.INVALID_OPERATION
      );
    }

    // Get blob URL
    const blobPath = nodeId; // nodeId is the full path
    const url = `https://${this.config.storageAccount}.blob.core.windows.net/${this.config.container}/${blobPath}`;

    try {
      const token = await this._getToken();
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-ms-version': '2020-04-08'
        }
      });

      if (!response.ok) {
        throw new TreeDataProviderError(
          `Failed to download file: ${response.statusText}`,
          TreeDataProviderErrorCode.NETWORK_ERROR,
          response.status
        );
      }

      return await response.blob();
    } catch (error) {
      if (error instanceof TreeDataProviderError) {
        throw error;
      }
      throw new TreeDataProviderError(
        'Failed to get file content',
        TreeDataProviderErrorCode.NETWORK_ERROR,
        undefined,
        error as Error
      );
    }
  }

  /**
   * Refresh data from source
   */
  async refresh(): Promise<void> {
    this._invalidateCache();
    await this.getRootNodes();
    this._emit('changed', {});
  }

  /**
   * Search for nodes
   */
  async search(query: string): Promise<TreeNode[]> {
    // Ensure tree is loaded
    if (!this.rootNodes) {
      await this.getRootNodes();
    }

    const lowerQuery = query.toLowerCase();
    const results: TreeNode[] = [];

    this.cache.forEach((node) => {
      if (node.name.toLowerCase().includes(lowerQuery) ||
          node.path.toLowerCase().includes(lowerQuery)) {
        results.push(node);
      }
    });

    return results;
  }

  // ==================== PUBLIC: CREATE OPERATIONS ====================

  /**
   * Create a new folder
   */
  async createFolder(options: CreateNodeOptions): Promise<TreeNode> {
    const { parentId, name } = options;

    // Validate name
    const validation = this.validateFileName(name, 'folder');
    if (!validation.isValid) {
      throw new TreeDataProviderError(
        validation.error || 'Invalid folder name',
        TreeDataProviderErrorCode.INVALID_NAME
      );
    }

    // Build path
    const parentPath = parentId ? this.cache.get(parentId)?.path || '' : '';
    const folderPath = this._joinPath(parentPath, name);

    // Check if already exists
    if (this.cache.has(folderPath)) {
      throw new TreeDataProviderError(
        'Folder already exists',
        TreeDataProviderErrorCode.ALREADY_EXISTS
      );
    }

    // Create marker blob for folder
    const markerPath = `${folderPath}/.folder`;

    try {
      await this._putBlob(markerPath, '', 'application/octet-stream');
    } catch (error) {
      throw new TreeDataProviderError(
        'Failed to create folder',
        TreeDataProviderErrorCode.UNKNOWN,
        undefined,
        error as Error
      );
    }

    // Create TreeNode
    const node: TreeNode = {
      id: folderPath,
      name,
      type: 'folder',
      path: folderPath,
      icon: this._getFolderIcon(),
      parentId,
      items: [],
      childrenLoaded: true,
      metadata: {
        modified: new Date()
      }
    };

    // Update cache and parent
    this.cache.set(node.id, node);

    if (parentId) {
      const parent = this.cache.get(parentId);
      if (parent && parent.items) {
        parent.items.push(node);
      }
    } else {
      this.rootNodes?.push(node);
    }

    // Emit event
    this._emit('created', { node });

    return node;
  }

  /**
   * Create a new file
   */
  async createFile(options: CreateNodeOptions): Promise<TreeNode> {
    const { parentId, name, content, contentType } = options;

    // Validate name
    const validation = this.validateFileName(name, 'file');
    if (!validation.isValid) {
      throw new TreeDataProviderError(
        validation.error || 'Invalid file name',
        TreeDataProviderErrorCode.INVALID_NAME
      );
    }

    // Build path
    const parentPath = parentId ? this.cache.get(parentId)?.path || '' : '';
    const filePath = this._joinPath(parentPath, name);

    // Check if already exists
    if (this.cache.has(filePath)) {
      throw new TreeDataProviderError(
        'File already exists',
        TreeDataProviderErrorCode.ALREADY_EXISTS
      );
    }

    // Default content for new diagram files
    const defaultContent = content || this._getDefaultFileContent(name);
    const finalContentType = contentType || this._getContentType(name);

    try {
      await this._putBlob(filePath, defaultContent, finalContentType);
    } catch (error) {
      throw new TreeDataProviderError(
        'Failed to create file',
        TreeDataProviderErrorCode.UNKNOWN,
        undefined,
        error as Error
      );
    }

    // Calculate size
    const size = typeof defaultContent === 'string'
      ? new Blob([defaultContent]).size
      : (defaultContent as Blob).size;

    // Create TreeNode
    const node: TreeNode = {
      id: filePath,
      name,
      type: 'file',
      path: filePath,
      icon: this._getFileIcon(name),
      parentId,
      childrenLoaded: true,
      metadata: {
        extension: name.split('.').pop(),
        size,
        modified: new Date(),
        contentType: finalContentType
      }
    };

    // Update cache and parent
    this.cache.set(node.id, node);

    if (parentId) {
      const parent = this.cache.get(parentId);
      if (parent && parent.items) {
        parent.items.push(node);
      }
    } else {
      this.rootNodes?.push(node);
    }

    // Emit event
    this._emit('created', { node });

    return node;
  }

  // ==================== PUBLIC: UPDATE OPERATIONS ====================

  /**
   * Rename a node
   */
  async rename(nodeId: string, newName: string): Promise<TreeNode> {
    const node = this.cache.get(nodeId);

    if (!node) {
      throw new TreeDataProviderError(
        'Node not found',
        TreeDataProviderErrorCode.NOT_FOUND
      );
    }

    // Validate new name
    const validation = this.validateFileName(newName, node.type);
    if (!validation.isValid) {
      throw new TreeDataProviderError(
        validation.error || 'Invalid name',
        TreeDataProviderErrorCode.INVALID_NAME
      );
    }

    // Build new path
    const parentPath = node.parentId ? this.cache.get(node.parentId)?.path || '' : '';
    const newPath = this._joinPath(parentPath, newName);

    // Check if new name already exists
    if (this.cache.has(newPath)) {
      throw new TreeDataProviderError(
        'A file or folder with this name already exists',
        TreeDataProviderErrorCode.ALREADY_EXISTS
      );
    }

    // For files: copy to new location, delete old
    // For folders: would need to rename all children (complex, not implemented yet)
    if (node.type === 'folder') {
      throw new TreeDataProviderError(
        'Renaming folders is not yet supported',
        TreeDataProviderErrorCode.INVALID_OPERATION
      );
    }

    try {
      // Step 1: Get the current blob content
      console.log('[AzureStorageTreeProvider] Renaming file from:', node.path, 'to:', newPath);
      const blobContent = await this.getFileContent(nodeId);

      // Step 2: Upload to new path
      await this._putBlob(newPath, blobContent, node.metadata?.contentType || 'application/octet-stream');

      // Step 3: Delete old blob
      await this._deleteBlob(node.path);

      // Step 4: Update cache - remove old node
      this.cache.delete(nodeId);

      // Step 5: Create new node with updated name and path
      const renamedNode: TreeNode = {
        ...node,
        id: newPath,
        name: newName,
        path: newPath,
        metadata: {
          ...node.metadata,
          modified: new Date()
        }
      };

      // Step 6: Update parent's items array
      if (node.parentId) {
        const parent = this.cache.get(node.parentId);
        if (parent && parent.items) {
          // Remove old node from parent's items
          const oldIndex = parent.items.findIndex(item => item.id === nodeId);
          if (oldIndex !== -1) {
            parent.items.splice(oldIndex, 1);
          }
          // Add renamed node to parent's items
          parent.items.push(renamedNode);
          // Sort items by name
          parent.items.sort((a, b) => a.name.localeCompare(b.name));
        }
      } else {
        // Root level - update rootNodes
        const oldIndex = this.rootNodes?.findIndex(item => item.id === nodeId) ?? -1;
        if (oldIndex !== -1 && this.rootNodes) {
          this.rootNodes.splice(oldIndex, 1);
          this.rootNodes.push(renamedNode);
          this.rootNodes.sort((a, b) => a.name.localeCompare(b.name));
        }
      }

      // Step 7: Add renamed node to cache
      this.cache.set(newPath, renamedNode);

      // Step 8: Emit renamed event
      this._emit('renamed', { node: renamedNode });

      console.log('[AzureStorageTreeProvider] File renamed successfully');
      return renamedNode;

    } catch (error) {
      console.error('[AzureStorageTreeProvider] Rename failed:', error);
      throw new TreeDataProviderError(
        `Failed to rename: ${(error as Error).message}`,
        TreeDataProviderErrorCode.UNKNOWN,
        undefined,
        error as Error
      );
    }
  }

  /**
   * Move/rename a blob to a new path
   * This is essentially a blob rename in Azure Storage
   */
  async move(nodeId: string, newParentId: string | null, newName?: string): Promise<TreeNode> {
    const node = this.cache.get(nodeId);

    if (!node) {
      throw new TreeDataProviderError(
        'Node not found',
        TreeDataProviderErrorCode.NOT_FOUND
      );
    }

    // Use provided name or keep the same name
    const finalName = newName || node.name;

    // Build new path - just concatenate parent path with name
    const newParentPath = newParentId || '';
    const newPath = newParentPath ? `${newParentPath}/${finalName}` : finalName;

    // Check if target already exists
    if (this.cache.has(newPath)) {
      throw new TreeDataProviderError(
        'A file or folder with this name already exists in the target location',
        TreeDataProviderErrorCode.ALREADY_EXISTS
      );
    }

    // For folders: would need to move all children (complex, not implemented yet)
    if (node.type === 'folder') {
      throw new TreeDataProviderError(
        'Moving folders is not yet supported',
        TreeDataProviderErrorCode.INVALID_OPERATION
      );
    }

    try {
      // Step 1: Get the current blob content
      console.log('[AzureStorageTreeProvider] Moving file from:', node.path, 'to:', newPath);
      const blobContent = await this.getFileContent(nodeId);

      // Step 2: Upload to new location
      await this._putBlob(newPath, blobContent, node.metadata?.contentType || 'application/octet-stream');

      // Step 3: Delete old blob
      await this._deleteBlob(node.path);

      // Step 4: Update cache - remove old node
      this.cache.delete(nodeId);

      // Step 5: Remove from old parent
      if (node.parentId) {
        const oldParent = this.cache.get(node.parentId);
        if (oldParent && oldParent.items) {
          const oldIndex = oldParent.items.findIndex(item => item.id === nodeId);
          if (oldIndex !== -1) {
            oldParent.items.splice(oldIndex, 1);
          }
        }
      } else {
        // Remove from root nodes
        const oldIndex = this.rootNodes?.findIndex(item => item.id === nodeId) ?? -1;
        if (oldIndex !== -1 && this.rootNodes) {
          this.rootNodes.splice(oldIndex, 1);
        }
      }

      // Step 6: Create moved node with new path and parent
      const movedNode: TreeNode = {
        ...node,
        id: newPath,
        path: newPath,
        parentId: newParentId,
        metadata: {
          ...node.metadata,
          modified: new Date()
        }
      };

      // Step 7: Refresh the tree to rebuild structure from blobs
      // This handles creating virtual folders automatically
      await this.refresh();

      // Step 8: Get the moved node from the refreshed cache
      const refreshedNode = this.cache.get(newPath);
      if (!refreshedNode) {
        throw new TreeDataProviderError(
          'Failed to find moved node after refresh',
          TreeDataProviderErrorCode.UNKNOWN
        );
      }

      // Step 9: Emit moved event
      this._emit('moved', { node: refreshedNode });

      console.log('[AzureStorageTreeProvider] File moved successfully');
      return refreshedNode;

    } catch (error) {
      console.error('[AzureStorageTreeProvider] Move failed:', error);
      throw new TreeDataProviderError(
        `Failed to move: ${(error as Error).message}`,
        TreeDataProviderErrorCode.UNKNOWN,
        undefined,
        error as Error
      );
    }
  }

  // ==================== PUBLIC: DELETE OPERATIONS ====================

  /**
   * Delete a node
   */
  async delete(nodeId: string): Promise<void> {
    const node = this.cache.get(nodeId);

    if (!node) {
      throw new TreeDataProviderError(
        'Node not found',
        TreeDataProviderErrorCode.NOT_FOUND
      );
    }

    // For folders, delete all children first
    if (node.type === 'folder' && node.items && node.items.length > 0) {
      // Delete all children
      for (const child of node.items) {
        await this.delete(child.id);
      }

      // Delete folder marker
      const markerPath = `${node.path}/.folder`;
      try {
        await this._deleteBlob(markerPath);
      } catch (error) {
        // Ignore errors deleting marker
      }
    } else if (node.type === 'file') {
      // Delete the file blob
      await this._deleteBlob(node.path);
    }

    // Remove from cache
    this.cache.delete(node.id);

    // Remove from parent's items
    if (node.parentId) {
      const parent = this.cache.get(node.parentId);
      if (parent && parent.items) {
        const index = parent.items.findIndex(n => n.id === node.id);
        if (index !== -1) {
          parent.items.splice(index, 1);
        }
      }
    } else {
      // Remove from root nodes
      if (this.rootNodes) {
        const index = this.rootNodes.findIndex(n => n.id === node.id);
        if (index !== -1) {
          this.rootNodes.splice(index, 1);
        }
      }
    }

    // Emit event
    this._emit('deleted', { node });
  }

  // ==================== PUBLIC: VALIDATION ====================

  /**
   * Validate a file or folder name
   */
  validateFileName(name: string, type: 'folder' | 'file'): FileNameValidationResult {
    // Check for empty name
    if (!name || name.trim().length === 0) {
      return {
        isValid: false,
        error: 'Name cannot be empty'
      };
    }

    // Check length
    if (name.length > 255) {
      return {
        isValid: false,
        error: 'Name is too long (maximum 255 characters)'
      };
    }

    // Check for invalid characters
    // Azure Blob Storage restrictions: / \ : * ? " < > |
    const invalidChars = /[\\:*?"<>|]/;
    if (invalidChars.test(name)) {
      return {
        isValid: false,
        error: 'Name contains invalid characters (\\:*?"<>|)'
      };
    }

    // Check for path separators (/ is valid in blob names but not in our node names)
    if (name.includes('/')) {
      return {
        isValid: false,
        error: 'Name cannot contain forward slashes (/)'
      };
    }

    // Check for reserved names
    const reserved = ['.', '..'];
    if (reserved.includes(name)) {
      return {
        isValid: false,
        error: 'Name is reserved'
      };
    }

    // Check for names that start or end with whitespace
    if (name !== name.trim()) {
      return {
        isValid: false,
        error: 'Name cannot start or end with whitespace'
      };
    }

    return { isValid: true };
  }

  // ==================== PUBLIC: EVENTS ====================

  /**
   * Subscribe to events
   */
  on(event: TreeProviderEventType, handler: (detail: TreeProviderEventDetail) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  /**
   * Unsubscribe from events
   */
  off(event: TreeProviderEventType, handler: (detail: TreeProviderEventDetail) => void): void {
    const handlers = this.eventHandlers.get(event) || [];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  }
}
